import { useEffect, useState } from 'react';
import { supabase, Widget, Article, Category } from '../lib/supabase';
import { Search } from 'lucide-react';

type WidgetRendererProps = {
  position: string;
};

export default function WidgetRenderer({ position }: WidgetRendererProps) {
  const [widgets, setWidgets] = useState<Widget[]>([]);

  useEffect(() => {
    loadWidgets();
  }, [position]);

  async function loadWidgets() {
    const { data } = await supabase
      .from('widgets')
      .select('*')
      .eq('position', position)
      .eq('is_active', true)
      .order('display_order');

    if (data) {
      setWidgets(data);
    }
  }

  return (
    <div className="space-y-6">
      {widgets.map((widget) => (
        <WidgetContent key={widget.id} widget={widget} />
      ))}
    </div>
  );
}

function WidgetContent({ widget }: { widget: Widget }) {
  const [recentArticles, setRecentArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    if (widget.type === 'recent_articles') {
      loadRecentArticles();
    } else if (widget.type === 'categories') {
      loadCategories();
    }
  }, [widget.type]);

  async function loadRecentArticles() {
    const limit = widget.settings?.limit || 5;
    const { data } = await supabase
      .from('articles')
      .select('id, title, slug, published_at, main_image_url, main_image_alt')
      .eq('status', 'published')
      .order('published_at', { ascending: false })
      .limit(limit);

    if (data) {
      setRecentArticles(data as Article[]);
    }
  }

  async function loadCategories() {
    const { data } = await supabase
      .from('categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order');

    if (data) {
      setCategories(data);
    }
  }

  if (widget.type === 'recent_articles') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        {widget.title && <h3 className="text-xl font-bold mb-4 text-gray-900">{widget.title}</h3>}
        <div className="space-y-4">
          {recentArticles.map((article) => (
            <a
              key={article.id}
              href={`/${article.slug}`}
              className="block group hover:shadow-md transition-shadow rounded-lg overflow-hidden border border-gray-100"
            >
              {article.main_image_url && (
                <div className="w-full h-48 overflow-hidden bg-gray-100">
                  <img
                    src={article.main_image_url}
                    alt={article.main_image_alt || article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              <div className="p-3">
                <h4 className="text-blue-600 group-hover:text-blue-800 transition-colors font-medium">
                  {article.title}
                </h4>
              </div>
            </a>
          ))}
        </div>
      </div>
    );
  }

  if (widget.type === 'categories') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        {widget.title && <h3 className="text-xl font-bold mb-4 text-gray-900">{widget.title}</h3>}
        <div className="space-y-2">
          {categories.map((category) => {
            const href = category.url || `#category-${category.slug}`;
            const rel = category.nofollow ? 'nofollow' : undefined;

            return (
              <a
                key={category.id}
                href={href}
                rel={rel}
                className="block text-gray-700 hover:text-blue-600 transition-colors"
                target={category.url ? '_blank' : undefined}
              >
                {category.name}
                {widget.settings?.show_count && (
                  <span className="text-sm text-gray-500 ml-2"></span>
                )}
              </a>
            );
          })}
        </div>
      </div>
    );
  }

  if (widget.type === 'search') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        {widget.title && <h3 className="text-xl font-bold mb-4 text-gray-900">{widget.title}</h3>}
        <div className="relative">
          <input
            type="text"
            placeholder="Поиск..."
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Search className="absolute right-3 top-2.5 text-gray-400" size={20} />
        </div>
      </div>
    );
  }

  if (widget.type === 'custom_html') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        {widget.title && <h3 className="text-xl font-bold mb-4 text-gray-900">{widget.title}</h3>}
        <div dangerouslySetInnerHTML={{ __html: widget.content }} />
      </div>
    );
  }

  if (widget.type === 'newsletter') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        {widget.title && <h3 className="text-xl font-bold mb-4 text-gray-900">{widget.title}</h3>}
        <p className="text-sm text-gray-600 mb-4">
          Подпишитесь на нашу рассылку, чтобы получать новые рецепты
        </p>
        <div className="space-y-2">
          <input
            type="email"
            placeholder="Ваш email"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
            Подписаться
          </button>
        </div>
      </div>
    );
  }

  return null;
}
